if (!customElements.get('beadifique-collection-controls')) {
  customElements.define('beadifique-collection-controls', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.toolbar = document.querySelector(`toolbar-item[data-id="${this.dataset.sectionId}"]`);
      this.dialog = this.querySelector('dialog');
      if (!this.toolbar || !this.dialog) return;
      this.trigger = this.toolbar.querySelector('[data-bf-panel-open]');
      this.sorts = [...this.toolbar.querySelectorAll('[data-bf-sort]'), ...this.querySelectorAll('[data-bf-sort]')];
      this.mobile = window.matchMedia('(max-width: 1024px)');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.trigger.addEventListener('click', () => this.openPanel(), options);
      this.querySelectorAll('[data-bf-panel-close]').forEach(button => button.addEventListener('click', () => this.dialog.close(), options));
      this.dialog.addEventListener('close', () => this.restorePanel(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      }, options);
      this.mobile.addEventListener('change', () => {
        if (!this.mobile.matches && this.dialog.open) this.dialog.close();
      }, options);
      this.sorts.forEach(sort => {
        sort.addEventListener('change', event => {
          const radio = event.target.closest('[data-bf-criterion]');
          if (!radio) return;
          const values = [...sort.querySelector('[data-bf-sort-source]').options].map(option => option.value);
          const preferred = radio.value === 'created' ? `${radio.value}-descending` : `${radio.value}-ascending`;
          const value = values.includes(radio.value) ? radio.value : preferred;
          this.applySort(value, event);
          if (values.includes(radio.value)) sort.querySelector('details')?.removeAttribute('open');
        }, options);
        sort.addEventListener('click', event => {
          const button = event.target.closest('[data-bf-sort-value]');
          if (!button) return;
          this.applySort(button.dataset.bfSortValue, event);
          sort.querySelector('details')?.removeAttribute('open');
          sort.querySelector('summary')?.focus();
        }, options);
        sort.addEventListener('keydown', event => {
          if (event.key !== 'Escape' || !sort.querySelector('details[open]')) return;
          event.preventDefault();
          event.stopPropagation();
          sort.querySelector('details').open = false;
          sort.querySelector('summary').focus();
        }, options);
      });
      document.addEventListener('click', event => {
        this.sorts.forEach(sort => {
          if (!sort.contains(event.target)) sort.querySelector('details')?.removeAttribute('open');
        });
      }, options);
      document.addEventListener('input', event => {
        if (event.target.closest('#CollectionFiltersForm')) this.updateFilterCount();
      }, options);
      window.addEventListener('popstate', () => this.syncFromURL(), options);
      this.querySelector('[data-bf-clear]')?.addEventListener('click', event => {
        const form = document.querySelector('collection-filters-form');
        if (!form) return;
        const url = new URL(window.location.href);
        [...url.searchParams.keys()].forEach(key => {
          if (key.startsWith('filter.') || key === 'page') url.searchParams.delete(key);
        });
        form.querySelectorAll('input[name^="filter."]').forEach(input => {
          if (input.type === 'checkbox') input.checked = false;
          else if (input.name.startsWith('filter.v.price.')) input.value = '';
        });
        this.updateFilterCount();
        form.onActiveFilterClick({ preventDefault() {}, currentTarget: { href: url.href } });
        event.currentTarget.blur();
      }, options);
      this.querySelector('[data-bf-page-size]')?.addEventListener('change', event => {
        this.toolbar.querySelector(`[data-limited-view-item] .text[data-value="${event.target.value}"]`)?.click();
      }, options);
      this.querySelector('[data-bf-view-slot]')?.addEventListener('click', () => {
        queueMicrotask(() => this.views?.querySelectorAll('.icon-mode').forEach(button => button.setAttribute('aria-pressed', String(button.classList.contains('active')))));
      }, options);
      this.observer = new MutationObserver(records => {
        if (records.some(record => record.target.closest?.('.collection, #main-collection-filters') || record.attributeName === 'data-total-items')) this.syncFromURL();
      });
      const grid = document.getElementById('CollectionProductGrid');
      if (grid) this.observer.observe(grid, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-total-items'] });
      this.sidebarObserver = new MutationObserver(() => this.updateFilterCount());
      const sidebar = document.getElementById('main-collection-filters');
      if (sidebar) this.sidebarObserver.observe(sidebar, { childList: true, subtree: true });
      this.syncFromURL();
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.restorePanel();
      this.abort?.abort();
      this.observer?.disconnect();
      this.sidebarObserver?.disconnect();
      this.ready = false;
    }

    openPanel() {
      if (!this.mobile.matches || this.dialog.open) return;
      this.sidebar = document.getElementById('halo-sidebar');
      const filterSlot = this.querySelector('[data-bf-filter-slot]');
      if (this.sidebar && filterSlot) {
        this.sidebarAnchor = document.createComment('collection-sidebar');
        this.sidebar.before(this.sidebarAnchor);
        filterSlot.append(this.sidebar);
      }
      this.views = this.toolbar.querySelector('[data-view-as-mobile]');
      const viewSlot = this.querySelector('[data-bf-view-slot]');
      if (this.views && viewSlot) {
        this.viewsAnchor = document.createComment('collection-views');
        this.views.before(this.viewsAnchor);
        viewSlot.append(this.views);
      }
      this.sorts.forEach(sort => sort.querySelector('details')?.removeAttribute('open'));
      document.body.classList.add('bf-collection-panel-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.dialog.showModal();
      this.updateFilterCount();
    }

    restorePanel() {
      if (this.sidebarAnchor?.isConnected && this.sidebar) this.sidebarAnchor.replaceWith(this.sidebar);
      if (this.viewsAnchor?.isConnected && this.views) this.viewsAnchor.replaceWith(this.views);
      this.sidebarAnchor = null;
      this.viewsAnchor = null;
      document.body.classList.remove('bf-collection-panel-open');
      this.trigger?.setAttribute('aria-expanded', 'false');
    }

    applySort(value, event) {
      const source = this.sorts[0]?.querySelector('[data-bf-sort-source]');
      if (!source || ![...source.options].some(option => option.value === value)) return;
      this.syncSort(value);
      const filters = document.querySelector('collection-filters-form');
      const filterSort = filters?.querySelector('[name="sort_by"]');
      if (filterSort) {
        filterSort.value = value;
        filters.onSubmitHandlerFromSortBy(event, filters.querySelector('form'));
      } else {
        this.toolbar.queryParams();
        this.toolbar.searchParams.set('sort_by', value);
        this.toolbar.searchParams.delete('page');
        this.toolbar.renderPage(this.toolbar.createURLHash());
      }
    }

    syncFromURL() {
      const value = new URL(window.location.href).searchParams.get('sort_by') || this.sorts[0]?.dataset.defaultSort;
      if (value) this.syncSort(value);
      this.updateFilterCount();
    }

    syncSort(value) {
      const criterion = value.replace(/-(ascending|descending)$/, '');
      this.sorts.forEach(sort => {
        const source = sort.querySelector('[data-bf-sort-source]');
        const option = [...source.options].find(item => item.value === value);
        if (!option) return;
        source.value = value;
        const current = sort.querySelector('[data-bf-sort-current]');
        if (current && current.textContent !== option.textContent) current.textContent = option.textContent;
        sort.querySelectorAll('[data-bf-criterion]').forEach(radio => { radio.checked = radio.value === criterion; });
        sort.querySelector('.bf-sort-directions').hidden = criterion === value;
        sort.querySelectorAll('[data-bf-sort-value]').forEach(button => {
          button.hidden = button.dataset.bfDirectionGroup !== criterion;
          button.setAttribute('aria-pressed', String(button.dataset.bfSortValue === value));
        });
      });
      const filterSort = document.querySelector('#CollectionFiltersForm [name="sort_by"]');
      if (filterSort) filterSort.value = value;
    }

    updateFilterCount() {
      const form = document.getElementById('CollectionFiltersForm');
      let count = form?.querySelectorAll('input[type="checkbox"]:checked').length || 0;
      if (form && [...form.querySelectorAll('input[name^="filter.v.price."]')].some(input => input.value !== '')) count += 1;
      const badge = this.trigger.querySelector('[data-bf-filter-count]');
      if (badge.textContent !== String(count)) badge.textContent = String(count);
      badge.hidden = count === 0;
      const clear = this.querySelector('[data-bf-clear]');
      if (clear) clear.disabled = count === 0;
    }
  });
}
